import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

// Page transition wrapper
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

// Animated section component
function AnimatedSection({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Image modal for viewing larger images
function ImageModal({ image, onClose, title }) {
  return (
    <motion.div
      className="image-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="image-modal-content"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>×</button>
        <img src={image} alt={title} />
        {title && <p className="modal-caption">{title}</p>}
      </motion.div>
    </motion.div>
  );
}

function OliviaRodrigoStandalone() {
  const [selectedImage, setSelectedImage] = useState(null);

  // Room 1 mockup galleries with inspiration images
  const room1Galleries = [
    {
      id: 'room-overview',
      title: 'Overall Room Shot',
      mockup: '/images/concepts/olivia-rodrigo/room1/Mockup-RoomOverview.png',
      description: 'The complete "drivers license" room featuring the projection corner, Mercedes photo op, suburban backdrop, party booth, and intimate piano nook.',
      inspirations: [
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo5.png', caption: 'White Mercedes-Benz from Grammy performance' },
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo6.png', caption: 'Suburban street with mountains (projection content)' },
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo7.png', caption: 'Tinsel party booth, "I GOT MY DRIVERS LICENSE" banner, balloons' },
      ],
    },
    {
      id: 'projection-room',
      title: 'Projection Room Shot',
      mockup: '/images/concepts/olivia-rodrigo/room1/Mockup-ProjectionRoom.png',
      description: 'The hero photo moment. "YOU SAID FOREVER" text wraps across walls, floor, and onto visitors who step into the corner alcove.',
      inspirations: [
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo1.png', caption: 'Corner projection setup, text on walls/floor/body' },
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo2.png', caption: 'Text projected onto skin, magenta/red/amber tones' },
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo3.png', caption: '"YOU SAID FOREVER" repeating pattern, deep red lighting' },
      ],
    },
    {
      id: 'piano-corner',
      title: 'Piano Corner Shot',
      mockup: '/images/concepts/olivia-rodrigo/room1/Mockup-Piano-View.png',
      description: 'The intimate origin story moment, recreating where it all began with red velvet curtains, warm amber lighting, and handwritten lyrics.',
      inspirations: [
        { image: '/images/concepts/olivia-rodrigo/room1/Room1-inspo9.png', caption: 'Red velvet curtain backdrop, intimate piano angle, hands on keys' },
      ],
    },
  ];

  return (
    <motion.div
      className="concepts-page olivia-page standalone-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      {/* Standalone Header */}
      <header className="standalone-header">
        <div className="container">
          <span className="standalone-name">Samantha de Nicola</span>
        </div>
      </header>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <ImageModal
            image={selectedImage.image}
            title={selectedImage.title}
            onClose={() => setSelectedImage(null)}
          />
        )}
      </AnimatePresence>

      {/* Page Hero */}
      <section className="page-hero playful olivia-hero">
        <div className="container">
          <motion.div
            className="page-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="concept-meta">
              <span className="concept-category">Fan Activation Concept</span>
              <span className="concept-status">Speculative Design</span>
            </div>
            <h1 className="page-title olivia-title">&ldquo;Drivers License: The Anniversary Experience&rdquo;</h1>
            <p className="page-subtitle">A Five-Year Celebration of the Song That Changed Everything</p>
          </motion.div>
        </div>
        <div className="hero-decoration" />
      </section>

      {/* Featured Concept Content */}
      <section className="featured-concept">
        <div className="container">
          {/* Concept Overview */}
          <AnimatedSection className="concept-overview" delay={0.1}>
            <div className="overview-content">
              <p className="overview-lead">
                A modular, artist-specific immersive pop-up concept designed to celebrate the
                five-year anniversary of Olivia Rodrigo's breakthrough single "drivers license."
              </p>
              <p>
                This speculative design translates the emotional universe of the song (nostalgia, heartbreak,
                first loves, and coming-of-age moments) into an immersive space where
                fans become part of the art. Beyond the single itself, the experience weaves in iconic
                moments from Olivia's career since: the Grammy wins, the SOUR and GUTS eras, viral fan
                moments, and the cultural touchstones that turned a breakout hit into a defining artist
                journey. Each room builds on a different chapter, giving fans a chance to relive the
                moments that made them fall in love with her music.
              </p>
            </div>
          </AnimatedSection>

          {/* Strategic Framework */}
          <AnimatedSection className="strategic-framework" delay={0.2}>
            <h3>Strategic Framework</h3>
            <div className="framework-grid">
              <div className="framework-card">
                <h4>Revenue Levers</h4>
                <p>
                  Exclusive on-site merch, limited vinyl pressings, and physical keepsakes
                  drive ticket sales while deepening fan connection to her discography.
                </p>
              </div>
              <div className="framework-card">
                <h4>PR & Visibility</h4>
                <p>
                  Every room optimized for social content. Superfan early access
                  generates organic buzz before public opening.
                </p>
              </div>
              <div className="framework-card">
                <h4>Fan Connection</h4>
                <p>
                  Every detail rewards the superfan: Easter eggs from music videos, lyrics
                  embedded in the environment, and interactive moments that make visitors
                  feel like they're living inside Olivia's world.
                </p>
              </div>
              <div className="framework-card">
                <h4>Partnerships</h4>
                <p>
                  Brand partners underwrite premium tiers: VIP early entry, meet-and-greet
                  add-ons, and sponsored photo stations.
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Room 1 Header */}
          <AnimatedSection className="room-header" delay={0.3}>
            <div className="room-label">Room 1</div>
            <h3>The First Night</h3>
            <p className="room-arc">
              January 8, 2021. The night &ldquo;drivers license&rdquo; dropped and Olivia celebrated with
              fans on Instagram Live, watching the song climb the charts in real time. This room
              recreates that magic: the photoshoots that defined the visual era, the party moment
              with tinsel and balloons, and the intimate piano corner where it all began.
            </p>
          </AnimatedSection>

          {/* Mockup Gallery 1: Split inspo (1 left, 2 right) */}
          <AnimatedSection className="mockup-gallery gallery-pinboard">
            <div className="gallery-header">
              <h4>{room1Galleries[0].title}</h4>
              <p>{room1Galleries[0].description}</p>
            </div>

            <div className="pinboard-layout split-inspo">
              {/* Left inspo - 1 photo */}
              <div className="pinboard-inspo inspo-left-side">
                <div className="inspo-tag">Reference</div>
                <div className="inspo-polaroid inspo-1">
                  <div className="polaroid-frame">
                    <img src={room1Galleries[0].inspirations[0].image} alt={room1Galleries[0].inspirations[0].caption} />
                    <p className="polaroid-caption">{room1Galleries[0].inspirations[0].caption}</p>
                  </div>
                </div>
              </div>

              {/* Center mockup */}
              <div
                className="pinboard-mockup"
                onClick={() => setSelectedImage({ image: room1Galleries[0].mockup, title: room1Galleries[0].title })}
              >
                <img src={room1Galleries[0].mockup} alt={room1Galleries[0].title} />
                <div className="mockup-label">Concept Render</div>
              </div>

              {/* Right inspo - 2 photos */}
              <div className="pinboard-inspo inspo-right-side">
                {room1Galleries[0].inspirations.slice(1).map((inspo, i) => (
                  <div
                    key={i}
                    className={`inspo-polaroid inspo-${i + 2}`}
                  >
                    <div className="polaroid-frame">
                      <img src={inspo.image} alt={inspo.caption} />
                      <p className="polaroid-caption">{inspo.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Explanation for Overall Room */}
            <div className="mockup-explanation">
              <p>
                Fans enter to see the white Mercedes-Benz from the Grammy performance with headlights
                cutting through purple haze. A suburban street projection scrolls behind it. The
                &ldquo;I GOT MY DRIVERS LICENSE&rdquo; party booth recreates that viral Instagram Live moment.
                The intimate piano corner sits in the back.
              </p>
              <p>
                &ldquo;YOU SAID FOREVER&rdquo; projects across the floor, creating continuity throughout
                the space. Each area offers a different photo opportunity and emotional beat.
              </p>
            </div>
          </AnimatedSection>

          {/* Mockup Gallery 2: Split inspo (2 left, 1 right) */}
          <AnimatedSection className="mockup-gallery gallery-pinboard">
            <div className="gallery-header">
              <h4>{room1Galleries[1].title}</h4>
              <p>{room1Galleries[1].description}</p>
            </div>

            <div className="pinboard-layout split-inspo">
              {/* Left inspo - 2 photos */}
              <div className="pinboard-inspo inspo-left-side">
                <div className="inspo-tag">Reference</div>
                {room1Galleries[1].inspirations.slice(0, 2).map((inspo, i) => (
                  <div
                    key={i}
                    className={`inspo-polaroid inspo-${i + 1}`}
                  >
                    <div className="polaroid-frame">
                      <img src={inspo.image} alt={inspo.caption} />
                      <p className="polaroid-caption">{inspo.caption}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Center mockup */}
              <div
                className="pinboard-mockup"
                onClick={() => setSelectedImage({ image: room1Galleries[1].mockup, title: room1Galleries[1].title })}
              >
                <img src={room1Galleries[1].mockup} alt={room1Galleries[1].title} />
                <div className="mockup-label">Concept Render</div>
              </div>

              {/* Right inspo - 1 photo */}
              <div className="pinboard-inspo inspo-right-side">
                <div className="inspo-polaroid inspo-3">
                  <div className="polaroid-frame">
                    <img src={room1Galleries[1].inspirations[2].image} alt={room1Galleries[1].inspirations[2].caption} />
                    <p className="polaroid-caption">{room1Galleries[1].inspirations[2].caption}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Explanation for Projection Room */}
            <div className="mockup-explanation">
              <p>
                <strong>The "Forever Room":</strong> This is THE hero photo moment of the entire experience.
                Fans step into a corner alcove where "YOU SAID FOREVER" text scrolls and repeats across every
                surface: walls, floor, and onto their own body. Deep crimson red and warm amber tones create
                that signature promo imagery look.
              </p>
              <p>
                Atmospheric haze catches the projector beams, adding depth and drama. Visitors literally become
                part of the art as lyrics wrap onto their face, arms, and clothes. Designed for maximum
                shareability. This is the shot that ends up on TikTok and Instagram.
              </p>
            </div>
          </AnimatedSection>

          {/* Mockup Gallery 3: Single inspo on right */}
          <AnimatedSection className="mockup-gallery gallery-pinboard">
            <div className="gallery-header">
              <h4>{room1Galleries[2].title}</h4>
              <p>{room1Galleries[2].description}</p>
            </div>

            <div className="pinboard-layout single-inspo-layout">
              {/* Center mockup */}
              <div
                className="pinboard-mockup"
                onClick={() => setSelectedImage({ image: room1Galleries[2].mockup, title: room1Galleries[2].title })}
              >
                <img src={room1Galleries[2].mockup} alt={room1Galleries[2].title} />
                <div className="mockup-label">Concept Render</div>
              </div>

              {/* Right inspo - 1 photo */}
              <div className="pinboard-inspo inspo-right-side single-inspo">
                <div className="inspo-tag">Reference</div>
                <div className="inspo-polaroid inspo-1">
                  <div className="polaroid-frame">
                    <img src={room1Galleries[2].inspirations[0].image} alt={room1Galleries[2].inspirations[0].caption} />
                    <p className="polaroid-caption">{room1Galleries[2].inspirations[0].caption}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Explanation for Piano Corner */}
            <div className="mockup-explanation">
              <p>
                <strong>The Piano Corner:</strong> The quieter, more intimate beat. Deep red velvet
                curtains drape behind, matching her original video backdrop. A notebook with handwritten
                scribbles sits open on the music stand.
              </p>
              <p>
                Warm amber lighting creates a pocket of warmth against the cooler purple tones of the
                main space. Fans sit at the bench and recreate that raw, vulnerable moment of creativity.
              </p>
            </div>
          </AnimatedSection>

          {/* Room 1 Merch */}
          <AnimatedSection className="merch-section room-merch">
            <h3>Merch & Revenue Opportunities</h3>
            <p className="merch-intro">
              On-site exclusive merchandise creates urgency and drives ticket sales. These items are <em>only</em> available
              at the experience. Scarcity fuels demand.
            </p>
            <div className="merch-grid">
              <div className="merch-item">
                <h4>&ldquo;Drivers License – Original Cut&rdquo;</h4>
                <p>Limited vinyl pressing with alternative cover art, numbered and foil-stamped. Only available on-site.</p>
              </div>
              <div className="merch-item">
                <h4>SOUR Covers Collection</h4>
                <p>Merch celebrating the iconic artist covers of SOUR tracks, including David Byrne's viral acoustic version that introduced Olivia to a new generation.</p>
              </div>
              <div className="merch-item">
                <h4>Polaroid Keepsake</h4>
                <p>Instant-print photo with unique star branding. Fans leave with a physical memory.</p>
              </div>
              <div className="merch-item">
                <h4>Exclusive Apparel</h4>
                <p>&ldquo;YOU SAID FOREVER&rdquo; projection-style graphics, anniversary edition pieces.</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          ROOM 2: GUTS UNVEILED
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="featured-concept room-2-section">
        <div className="container">
          <AnimatedSection className="room-header">
            <div className="room-label">Room 2</div>
            <h3>GUTS Unveiled</h3>
            <p className="room-arc">
              Step inside the iconic GUTS tracklist announcement video. The mysterious bird's-eye view
              room comes to life as a fully explorable space filled with easter eggs, hidden meanings,
              and interactive moments.
            </p>
          </AnimatedSection>

          <AnimatedSection className="room-content-block">
            <div className="room-description">
              <p>
                When Olivia announced GUTS, she released a mesmerizing overhead video of herself moving
                through a cluttered, intimate room where every item was a teaser for the album. <em>&ldquo;Bad idea right&rdquo;</em> written
                in red lipstick on a vanity mirror. <em>&ldquo;Now I hold it like a grudge&rdquo;</em> printed on paper left on
                a chair. <em>&ldquo;Stranger&rdquo;</em> and <em>&ldquo;Obsessed&rdquo;</em> hidden on the piano and draped across a stray
                piece of clothing.
              </p>
              <p>
                This room recreates that space at full scale. Fans explore the same room from the video,
                discovering the same easter eggs in person. An overhead camera recreates the iconic bird's-eye
                shot so visitors can film their own version of the announcement video.
              </p>
            </div>

            <div className="room-features">
              <h4>Key Elements</h4>
              <ul>
                <li>Vanity mirror with <em>&ldquo;Bad idea right&rdquo;</em> in red lipstick</li>
                <li>Unmade bed for <em>&ldquo;making the bed&rdquo;</em></li>
                <li>Baby blue typewriter with purple-glittered GUTS keys where staff writes personalized Olivia-style lyrics for fans</li>
                <li>Birthday cake with &ldquo;18&rdquo; or &ldquo;19&rdquo; candles</li>
                <li>Faux yearbook showing Olivia as sole member of &ldquo;Book club&rdquo;</li>
                <li>Interactive keyboard that plays songs when you press specific lyric keys</li>
                <li>Overhead camera station for fans to recreate the viral video</li>
              </ul>
            </div>

            <div className="room-features">
              <h4>Fan Takeaways</h4>
              <ul>
                <li>GUTS tracklist paper where fans write their own &ldquo;comments&rdquo; on copies to take home</li>
                <li>Props and stickers recreated from the video (on-site exclusive merch)</li>
              </ul>
            </div>

            {/* Room 2 Gallery */}
            <div className="room2-gallery">
              <div className="room2-main-image">
                <div className="mockup-label">Concept Render</div>
                <img src="/images/concepts/olivia-rodrigo/room2/GUTS.png" alt="GUTS room concept render" />
              </div>
              <div className="room2-inspo">
                <div className="inspo-tag">Reference</div>
                <div className="polaroid-frame">
                  <img src="/images/concepts/olivia-rodrigo/room2/Guts-Room.gif" alt="Original GUTS announcement video reference" />
                  <p className="polaroid-caption">GUTS Announcement</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          THE HALLWAY: WALK DOWN OLIVIA'S CAREER LANE
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="featured-concept hallway-section">
        <div className="container">
          <AnimatedSection className="room-header">
            <div className="room-label">The Hallway</div>
            <h3>Walk Down Olivia's Career Lane</h3>
            <p className="room-arc">
              A wide, immersive hallway connecting the rooms, designed with the aesthetic of the
              &ldquo;Obsessed&rdquo; music video. Photo frames line the walls, each holding a piece of her journey.
            </p>
          </AnimatedSection>

          <AnimatedSection className="room-content-block">
            <div className="room-description">
              <p>
                This isn't just a transition space. It's a retrospective. The hallway captures iconic
                behind-the-scenes moments from Olivia's career: photos with producer Dan Nigro in the
                studio making SOUR and GUTS, candid shots from award shows, music video BTS from
                &ldquo;deja vu,&rdquo; &ldquo;good 4 u,&rdquo; and more.
              </p>
              <p>
                The design pulls directly from the &ldquo;Obsessed&rdquo; music video aesthetic: ornate frames,
                gallery-style walls, intimate lighting. Real photos from throughout the SOUR and GUTS
                eras feature fans in the audience, at meet-and-greets, and at events, celebrating the
                community that made her career possible.
              </p>
            </div>

            <div className="room-features">
              <h4>The Journey</h4>
              <ul>
                <li><strong>SOUR Memories:</strong> The debut era including album release, first Grammy wins, the cultural moment</li>
                <li><strong>GUTS Announcement:</strong> The anticipation, the teases, the reveal</li>
                <li><strong>GUTS Memories:</strong> Tour moments, viral clips, fan connections</li>
                <li><strong>Never-Before-Seen:</strong> Exclusive photos with stories as plaques beside them</li>
              </ul>
            </div>

            <div className="room-features">
              <h4>Design Notes</h4>
              <ul>
                <li>&ldquo;Obsessed&rdquo; music video aesthetic with ornate frames and moody lighting</li>
                <li>Mix of professional shots and candid moments</li>
                <li>Fans prominently featured alongside artist photos</li>
                <li>Story plaques with context and never-before-heard anecdotes</li>
              </ul>
            </div>

          </AnimatedSection>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          SUMMARY
          ═══════════════════════════════════════════════════════════════════ */}
      <section className="featured-concept">
        <div className="container">
          <AnimatedSection className="concept-summary">
            <div className="summary-content">
              <p>
                This three-room experience transforms Olivia Rodrigo's artistic journey into an
                emotional adventure fans can explore. Room 1 captures the magic of &ldquo;drivers license.&rdquo;
                Room 2 brings the mysterious GUTS announcement to life. The Hallway celebrates the full
                arc of her career with fans at the center.
              </p>
              <p className="summary-highlight">
                Every element is designed for both <em>social moments</em> and <em>genuine emotional resonance</em>.
                The kind of experience fans remember, talk about, and return to.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA - Simplified */}
      <section className="page-cta playful standalone-cta">
        <div className="container">
          <AnimatedSection className="cta-content-playful">
            <h2>Let's Bring <em>Worlds</em> to Life</h2>
            <a href="mailto:sami@samidenicola.com" className="btn btn-primary">
              Get in Touch
            </a>
          </AnimatedSection>
        </div>
      </section>
    </motion.div>
  );
}

export default OliviaRodrigoStandalone;
